import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { notificar } from '../utils/notify';
import '../styles/Contabilidade.css';

const Devedores = () => {
  const [devedores, setDevedores] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [mensagem, setMensagem] = useState('');
  useEffect(() => { notificar(mensagem); }, [mensagem]);
  const [filtroTexto, setFiltroTexto] = useState('');
  const [filtroOperador, setFiltroOperador] = useState('');
  const [operadoresUnicos, setOperadoresUnicos] = useState([]);

  // Modal de histórico
  const [clienteSelecionado, setClienteSelecionado] = useState(null);
  const [mostrarModal, setMostrarModal] = useState(false);
  const [historico, setHistorico] = useState([]);
  const [carregandoHistorico, setCarregandoHistorico] = useState(false);
  const [novaMensagem, setNovaMensagem] = useState('');
  const [enviando, setEnviando] = useState(false);

  const [dadosUsuario, setDadosUsuario] = useState(null);
  const [carregandoAcesso, setCarregandoAcesso] = useState(true);
  const [acessoNegado, setAcessoNegado] = useState(false);

  // ─── auth ───
  const verificarAcesso = useCallback(async () => {
    setCarregandoAcesso(true);
    const token = localStorage.getItem('access');
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/verificar-grupo/`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        setDadosUsuario(res.data);
        setAcessoNegado(res.data.grupo === 'rh');
      } else {
        setAcessoNegado(true);
      }
    } catch {
      setAcessoNegado(true);
    } finally {
      setCarregandoAcesso(false);
    }
  }, []);

  useEffect(() => { verificarAcesso(); }, [verificarAcesso]);

  const username = dadosUsuario?.name || dadosUsuario?.first_name || dadosUsuario?.usuario || 'usuário';

  // ─── carregar devedores ───
  const carregarDevedores = useCallback(async () => {
    setCarregando(true);
    setMensagem('');
    const token = localStorage.getItem('access');
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/clientes-devedores/`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const lista = res.data.devedores || [];
      setDevedores(lista);
      const ops = [...new Set(lista.map(d => d.operador).filter(Boolean))].sort();
      setOperadoresUnicos(ops);
    } catch (err) {
      setMensagem(`Erro ao carregar devedores: ${err.response?.data?.erro || err.message}`);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    if (!acessoNegado && !carregandoAcesso) carregarDevedores();
  }, [carregarDevedores, acessoNegado, carregandoAcesso]);

  // ─── histórico ───
  const abrirHistorico = async (devedor) => {
    setClienteSelecionado(devedor);
    setHistorico([]);
    setNovaMensagem('');
    setMostrarModal(true);
    setCarregandoHistorico(true);
    const token = localStorage.getItem('access');
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/historico-devedor/${devedor.numero_dominio}/`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setHistorico(res.data.historico || []);
    } catch {
      setMensagem('Erro ao carregar histórico');
    } finally {
      setCarregandoHistorico(false);
    }
  };

  const enviarMensagem = async () => {
    if (!novaMensagem.trim()) return;
    setEnviando(true);
    const token = localStorage.getItem('access');
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE}/api/historico-devedor/${clienteSelecionado.numero_dominio}/`,
        {
          mensagem: novaMensagem.trim(),
          empresa: clienteSelecionado.empresa,
          operador: clienteSelecionado.operador,
          usuario_criador: username,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        setHistorico(prev => [{
          id: res.data.id,
          empresa: clienteSelecionado.empresa,
          operador: clienteSelecionado.operador,
          mensagem: novaMensagem.trim(),
          usuario_criador: username,
          criado_em: res.data.criado_em,
        }, ...prev]);
        setNovaMensagem('');
      }
    } catch {
      setMensagem('Erro ao enviar mensagem');
    } finally {
      setEnviando(false);
    }
  };

  // ─── filtros ───
  const devedoresFiltrados = devedores.filter(d => {
    const texto = filtroTexto.toLowerCase();
    const matchTexto = !texto ||
      d.empresa?.toLowerCase().includes(texto) ||
      d.numero_dominio?.toString().includes(texto) ||
      d.regime?.toLowerCase().includes(texto);
    const matchOp = !filtroOperador || d.operador === filtroOperador;
    return matchTexto && matchOp;
  });

  const corBadgeDias = (dias) => {
    if (dias >= 180) return 'bg-danger';
    if (dias >= 90) return 'bg-warning text-dark';
    return 'bg-secondary';
  };

  // ─── loading / acesso negado ───
  if (carregandoAcesso) {
    return (
      <div className="container mt-5 text-center">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
          <div>
            <div className="spinner-border text-primary mb-3" role="status"></div>
            <p className="text-muted">Verificando permissões...</p>
          </div>
        </div>
      </div>
    );
  }

  if (acessoNegado) {
    return (
      <div className="container mt-5 text-center">
        <div className="alert alert-danger" role="alert">
          <i className="fas fa-lock fa-3x mb-3"></i>
          <h2>Acesso Negado</h2>
          <button className="btn btn-outline-primary mt-3" onClick={() => window.location.href = '/home'}>
            <i className="fas fa-home me-2"></i>Voltar para início
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="contabilidade-page">
      <div className="container mt-5 shadow p-4 rounded bg-white">

        {/* Cabeçalho */}
        <div className="header-wrapper d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center">
            <i className="fas fa-exclamation-circle header-icon me-2"></i>
            <h2 className="titulo-pagina m-0">
              Clientes Devedores — Documentos em Atraso +60 dias
            </h2>
          </div>
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => window.location.href = '/Contabilidade'}
          >
            <i className="fas fa-arrow-left me-2"></i>Voltar
          </button>
        </div>

        {/* Mensagens agora são exibidas como toast (react-toastify) */}

        {/* Filtros */}
        <div className="row mb-3 g-2 align-items-end">
          <div className="col-md-4">
            <label className="form-label fw-bold">Buscar empresa / domínio</label>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Digite para filtrar..."
              value={filtroTexto}
              onChange={e => setFiltroTexto(e.target.value)}
            />
          </div>
          <div className="col-md-3">
            <label className="form-label fw-bold">Operador</label>
            <select
              className="form-select form-select-sm"
              value={filtroOperador}
              onChange={e => setFiltroOperador(e.target.value)}
            >
              <option value="">Todos os operadores</option>
              {operadoresUnicos.map(op => (
                <option key={op} value={op}>{op}</option>
              ))}
            </select>
          </div>
          <div className="col-md-5 d-flex align-items-end gap-2">
            <button
              className="btn btn-outline-primary btn-sm"
              onClick={carregarDevedores}
              disabled={carregando}
            >
              <i className="fas fa-sync me-1"></i>Atualizar
            </button>
            <span className="badge bg-danger fs-6 ms-auto">
              <i className="fas fa-building me-1"></i>
              {devedoresFiltrados.length} cliente(s) em atraso
            </span>
          </div>
        </div>

        {/* Tabela */}
        <div className="table-responsive">
          {carregando ? (
            <div className="text-center mt-4">
              <div className="spinner-border" role="status"></div>
              <p>Carregando clientes...</p>
            </div>
          ) : devedoresFiltrados.length === 0 ? (
            <div className="alert alert-info text-center mt-3">
              <i className="fas fa-check-circle fa-2x mb-2 d-block text-success"></i>
              Nenhum cliente com mais de 60 dias de atraso encontrado.
            </div>
          ) : (
            <table className="table table-bordered table-hover text-center align-middle fs-6">
              <thead className="table-light">
                <tr>
                  <th>Nº Domínio</th>
                  <th>Empresa</th>
                  <th>Operador</th>
                  <th>Regime</th>
                  <th>Última Entrega</th>
                  <th>Próxima Entrega</th>
                  <th>Dias em Atraso</th>
                  <th>Status</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {devedoresFiltrados.map((d, i) => (
                  <tr key={i}>
                    <td><code>{d.numero_dominio || '-'}</code></td>
                    <td className="text-start fw-semibold">
                      <i className="fas fa-building me-2 text-muted"></i>
                      {d.empresa}
                    </td>
                    <td>{d.operador || '-'}</td>
                    <td>
                      <span className="badge bg-secondary">{d.regime || '-'}</span>
                    </td>
                    <td>{d.ultima_entrega || '-'}</td>
                    <td>
                      <span className="text-danger fw-semibold">{d.proxima_entrega || '-'}</span>
                    </td>
                    <td>
                      <span className={`badge ${corBadgeDias(d.dias_atraso)} fs-6`}>
                        <i className="fas fa-clock me-1"></i>
                        {d.dias_atraso} dias
                      </span>
                    </td>
                    <td>
                      <span className="badge bg-danger">{d.status_contabil || 'Atrasado'}</span>
                    </td>
                    <td>
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => abrirHistorico(d)}
                        title="Ver histórico de conversa"
                      >
                        <i className="fas fa-comments me-1"></i>
                        Histórico
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Modal de Histórico */}
      {mostrarModal && clienteSelecionado && (
        <div
          className="modal"
          tabIndex="-1"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 9999 }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content shadow-lg">

              <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-comments me-2"></i>
                    Histórico de Conversa
                  </h5>
                  <small className="opacity-75" style={{ color: '#6c757d' }}>
                    <i className="fas fa-building me-1"></i>
                    {clienteSelecionado.empresa}
                    {' · '}
                    <i className="fas fa-clock me-1"></i>
                    {clienteSelecionado.dias_atraso} dias em atraso
                  </small>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setMostrarModal(false)}
                ></button>
              </div>

              <div className="modal-body" style={{ maxHeight: '420px', overflowY: 'auto' }}>
                {carregandoHistorico ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-danger" role="status"></div>
                    <p className="mt-2 text-muted">Carregando histórico...</p>
                  </div>
                ) : historico.length === 0 ? (
                  <div className="alert alert-info text-center">
                    <i className="fas fa-inbox fa-2x mb-2 d-block"></i>
                    Nenhum registro de conversa ainda. Seja o primeiro a registrar!
                  </div>
                ) : (
                  <div className="d-flex flex-column gap-3">
                    {historico.map((item) => (
                      <div
                        key={item.id}
                        className="card border-0 shadow-sm"
                        style={{ borderLeft: '4px solid #dc3545' }}
                      >
                        <div className="card-body py-2 px-3">
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <small className="fw-bold text-danger">
                              <i className="fas fa-user-circle me-1"></i>
                              {item.usuario_criador || item.operador || 'Usuário'}
                            </small>
                            <small className="text-muted">
                              <i className="fas fa-calendar-alt me-1"></i>
                              {item.criado_em}
                            </small>
                          </div>
                          <p className="mb-0" style={{ whiteSpace: 'pre-wrap', fontSize: '0.95rem' }}>
                            {item.mensagem}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Nova mensagem */}
              <div className="modal-footer flex-column align-items-stretch gap-2 bg-light">
                <label className="fw-bold text-muted small mb-0">
                  <i className="fas fa-pen me-1"></i>
                  Novo registro de contato / observação:
                </label>
                <textarea
                  className="form-control"
                  rows={3}
                  placeholder="Descreva o contato com o cliente, combinado, situação atual..."
                  value={novaMensagem}
                  onChange={e => setNovaMensagem(e.target.value)}
                  disabled={enviando}
                />
                <div className="d-flex justify-content-between align-items-center">
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => setMostrarModal(false)}
                  >
                    <i className="fas fa-times me-1"></i>Fechar
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    onClick={enviarMensagem}
                    disabled={enviando || !novaMensagem.trim()}
                  >
                    {enviando ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                        Salvando...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-paper-plane me-1"></i>
                        Salvar Registro
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Devedores;
